import { Collapse, makeStyles, Typography } from '@paintscout/ui'
import classNames from 'classnames'
import moment from 'moment'
import type { CalendarEvent } from 'paintscout'
import React, { useState } from 'react'
import { DayEvent } from '../day'

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column'
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(1),
    padding: theme.spacing(1, 1.5),
    backgroundColor: theme.paintscout.palette.brand.Neutral.P96,
    borderBottom: `1px solid ${theme.paintscout.palette.brand.Neutral.P90}`,
    cursor: 'pointer',
    '@media (hover: hover) and (pointer: fine)': {
      '&:hover': {
        backgroundColor: theme.paintscout.palette.brand.Neutral.P94
      }
    }
  },
  headerLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1)
  },
  dayName: {
    fontWeight: 500
  },
  dayNumber: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: '24px',
    height: '24px',
    padding: theme.spacing(0, 0.75),
    borderRadius: theme.borderRadius.md,
    backgroundColor: 'transparent',
    color: theme.paintscout.palette.brand.Neutral.P30,
    fontSize: theme.typography.pxToRem(13),
    fontWeight: 600,
    lineHeight: 1
  },
  todayDayNumber: {
    backgroundColor: theme.paintscout.palette.primary,
    color: theme.palette.common.white
  },
  countBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxSizing: 'border-box',
    minWidth: '28px',
    padding: theme.spacing(0.5, 1),
    borderRadius: theme.borderRadius.lg,
    backgroundColor: theme.palette.common.white,
    border: `1px solid ${theme.paintscout.palette.brand.Neutral.P85}`,
    color: theme.paintscout.palette.brand.Neutral.P60,
    fontSize: theme.typography.pxToRem(12),
    fontWeight: 500,
    lineHeight: 1,
    textAlign: 'center'
  },
  events: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    padding: theme.spacing(1.5),
    borderBottom: `1px solid ${theme.paintscout.palette.brand.Neutral.P90}`
  }
}))

interface ProjectsByDayProps {
  day: Date
  events: CalendarEvent[]
}

export function ProjectsByDay({ events, day }: ProjectsByDayProps) {
  const classes = useStyles()
  const isToday = moment(day).isSame(moment(), 'day')
  const [collapsed, setCollapsed] = useState(!isToday)

  // order events by total duration ascending
  const orderedEvents = events.sort((a, b) => {
    const aDuration = (a.end?.getTime() || 0) - (a.start?.getTime() || 0)
    const bDuration = (b.end?.getTime() || 0) - (b.start?.getTime() || 0)
    return aDuration - bDuration
  })

  const hasEvents = orderedEvents.length > 0

  return (
    <div className={classes.root}>
      <div className={classes.header} onClick={() => hasEvents && setCollapsed(!collapsed)}>
        <div className={classes.headerLabel}>
          <Typography className={classes.dayName}>{moment(day).format('dddd')}</Typography>
          <span
            className={classNames(classes.dayNumber, {
              [classes.todayDayNumber]: isToday
            })}
          >
            {moment(day).format('D')}
          </span>
        </div>
        {hasEvents && <span className={classes.countBadge}>{orderedEvents.length}</span>}
      </div>
      {hasEvents && (
        <Collapse show={!collapsed}>
          <div className={classes.events}>
            {orderedEvents.map((event) => (
              <DayEvent key={event.id} event={event} />
            ))}
          </div>
        </Collapse>
      )}
    </div>
  )
}
